import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { companyOf } from '../utils/company';
import { CheckCircle, XCircle, Check, ChevronDown, Printer, Copy, X, FileText } from 'lucide-react';

import { publicApi } from '../services/api';
import MowingOffer, { thenLine } from '../components/MowingOffer';
import BidLines from '../components/BidLines';
import BusinessProposal from '../components/BusinessProposal';
import BidExtras, { BidChoices, BidDelta } from '../components/BidExtras';
import ChangeOrderPublic, { ChangesDone } from '../components/ChangeOrderPublic';
import LawnWalkthroughButton from '../components/LawnWalkthroughButton';
import WinterChoice, { WinterOffer, WinterPick, WinterLinePick, useWinterChoice } from '../components/WinterChoice';
import BidOptions, { BidOptionsOffer, priceOf as optionsPrice, optionalLinesOf } from '../components/BidOptions';
import SizeChoice, { SizeOffer, SizePick, SizeLinePick, useSizeChoice } from '../components/SizeChoice';
import { SiteMapView, normalizeMarks, markColor, SiteMark } from '../components/SiteMapEditor';
import { longDay, monthWords, monthPicture } from '../utils/recurring';
import { money, longDate, clock } from '../utils/format';
import { OPENED_AFTER_MS, shouldSendOpened, openedContext, cameBackFromPayment, cameFromOffice, stripOfficeMark } from '../utils/bidOpened';
import {
  PublicPage, PublicLoading, DeadLink, Panel, H1, H2,
  ButtonPrimary, ButtonGhost, StickyBar, OptionRow,
} from '../components/public';

// The one billing sentence (server/services/billingCopy.js) — the server sends
// its own; this is the fallback so the page never says something different.
const BILLING_SENTENCE = 'We invoice after each completed visit. Pay by credit card or ACH when the invoice arrives.';
const BRANDS: Record<string, string> = { visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', discover: 'Discover' };
/** "Visa", "Mastercard" — Stripe's lower-case brand word, the way the office card prints it. */
export const brandWord = (b: string | null | undefined) => (b ? BRANDS[b] || `${b.charAt(0).toUpperCase()}${b.slice(1)}` : 'Card');

/**
 * The customer's quote page — the thing Michael sends.
 *
 * Rebuilt Sep 19 2026 against the Keep Austin Mowed design system. Three things
 * changed, all for the person reading it on a phone in their driveway:
 *
 *  1. The price and the one button now live in a bar pinned to the bottom of
 *     the screen. Before, both sat below roughly two thousand pixels of detail.
 *  2. Accepting is a sheet that asks one thing at a time — sign, then the code
 *     — instead of three text boxes stacked above the button. Two codes are
 *     never on screen together, which is how the wrong one got typed.
 *  3. Everything the customer may add or choose (BidExtras, the first-mow
 *     date in MowingOffer) sits above that bar, so the number on the button
 *     always includes it.
 *
 * Merged Sep 21 2026 with the bid work on main: the price as a picture
 * (MowingOffer), lines that explain themselves (BidLines), what they may add
 * (BidExtras), and lawn-plan bids where the three plans are the bid. Same
 * endpoints, same verification, same referral handling, same start-date
 * confirmation.
 */

export default function QuotePublic() {
  const { token } = useParams<{ token: string }>();
  const [done, setDone] = useState<'accepted' | 'declined' | null>(null);
  // "Remind me in a week" (the soft side of "Not right now"): the server's words, shown on the page; the bid stays open.
  const [snoozed, setSnoozed] = useState<string | null>(null);
  // Copy this quote's link (to forward to a spouse, or for the owner reading it on his phone).
  const [pageCopied, setPageCopied] = useState(false);
  const copyPageLink = () => { navigator.clipboard?.writeText(window.location.href).then(() => { setPageCopied(true); setTimeout(() => setPageCopied(false), 2000); }).catch(() => {}); };
  const [signedName, setSignedName] = useState('');
  const [referral, setReferral] = useState('');          // a neighbor's code, typed at the moment they say yes
  const [showReferral, setShowReferral] = useState(false);
  const [acceptNote, setAcceptNote] = useState<string | null>(null);
  // The options they took (server: services/bidOptions), in words, for the thank-you screen.
  const [tookOptions, setTookOptions] = useState<string[] | null>(null);
  const [startChoice, setStartChoice] = useState<string | null>(null);   // which first-mow date they picked (MowingOffer) — the server's key for it
  // What they added to their own bid (BidExtras): sent when they accept, priced again by the server.
  const [bidChoices, setBidChoices] = useState<BidChoices | null>(null);
  const [bidDelta, setBidDelta] = useState<BidDelta>({ monthly: 0, visit: 0, once: 0 });
  // "Do it in winter, save 10%" (WinterChoice): which lines they picked, and the server's saving for them. Starts from
  // what the server says is already winter work (a start day in the window) so the first total is right.
  const [winterPick, setWinterPick] = useState<WinterPick | null>(null);
  const [winterSaving, setWinterSaving] = useState<number | null>(null);
  // What accept booked for winter (reply.winter): the thank-you screen says it.
  const [winterBooked, setWinterBooked] = useState<{ words?: string } | null>(null);
  // Which plant size they picked on each planting line (SizeChoice), and what the server says the picks add to the
  // bid. Starts from the size the bid is on, so the first total is right.
  const [sizePick, setSizePick] = useState<SizePick | null>(null);
  const [sizeDelta, setSizeDelta] = useState(0);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [sheet, setSheet] = useState(false);             // the accept sheet
  // Signing is verified with a one-time code sent to the email (or phone) on file.
  const [verify, setVerify] = useState<{ to: string; via: string; options?: { email?: string | null; sms?: string | null }; practice_code?: string | null } | null>(null);
  const [code, setCode] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);
  // The day we mean to start, and their answer to it.
  // What accept booked, in the server's own dates and words (reply.scheduled): the first mow, the one after it and
  // "then every other Monday, from …". Nothing about dates is worked out on this page.
  const [booked, setBooked] = useState<{ start: string; day?: string | null; route_name?: string | null; first_visit?: string | null; next_visit?: string | null;
    then_words?: string | null; one_time?: boolean; is_start?: boolean } | null>(null);
  const [startAsk, setStartAsk] = useState<'idle' | 'form' | 'answered'>('idle');
  const [startNote, setStartNote] = useState('');
  const [startPreferred, setStartPreferred] = useState('');
  const [startThanks, setStartThanks] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery(
    ['public-quote', token],
    () => publicApi.get(`/quotes/public/${token}`).then(r => r.data),
    { enabled: !!token, retry: false }
  );
  const co = companyOf(data);

  // "Give me a notification if client opens the bid" (owner, Oct 20 2026): one beacon per page load, once the bid
  // has been on screen ~3 seconds, never from the office Preview, a browser signed in to the app, a link opened from
  // the app, or the bounce back from the card / deposit page (utils/bidOpened). Refetches never send it again; the
  // server texts him on the first open.
  const openedSent = useRef(false);
  const backFromPayment = useRef(cameBackFromPayment(window.location.search));
  // An office link (?from=office — the Preview's open-in-a-tab, the Share sheet's link): his own look, never counted.
  // Read once, then taken off the address bar so a link copied from this tab is the plain one.
  const fromOffice = useRef(cameFromOffice(window.location.search));
  useEffect(() => { if (fromOffice.current) stripOfficeMark(); }, []);
  const loadedId = data?.quote?.id;
  useEffect(() => {
    if (!loadedId || !token || openedSent.current) return;
    let timer: number | undefined;
    const arm = () => {
      window.clearTimeout(timer);
      if (openedSent.current || document.visibilityState !== 'visible') return;
      timer = window.setTimeout(() => {
        if (openedSent.current || !shouldSendOpened(openedContext(backFromPayment.current, fromOffice.current))) return;
        openedSent.current = true;
        publicApi.post(`/quotes/public/${token}/opened`).catch(() => {});
      }, OPENED_AFTER_MS);
    };
    arm();
    document.addEventListener('visibilitychange', arm);
    return () => { window.clearTimeout(timer); document.removeEventListener('visibilitychange', arm); };
  }, [loadedId, token]);

  const quote = data?.quote;
  // A business reads its bid as a business (server: quotes public GET → commercial { company, site, terms, po }).
  const commercial: { company: string; site: string | null; terms: number; po: string | null } | null = data?.commercial || null;
  // A monthly-contract business bid (server: services/contractBid): every price is a month, the term and the sales
  // tax on each monthly bill come with it, and the page reads "$X a month" and the term — never a visit or a year.
  const contract: { months: number; tax_pct: number; monthly: number; term: number; tax_month: number; start: string | null } | null = data?.contract || null;
  const isContract = !!contract;
  const lineItems: any[] = data?.lineItems || [];
  const groups: any[] = data?.groups || [];
  const media: any[] = data?.media || [];
  // Several options on one bid (server: services/bidOptions) — each its own title, photos and priced lines, and
  // theirs to tick one or more of. Null on every bid that has none, which is every bid until today: the page below
  // is then exactly what it was, because `lineItems` already holds every line.
  const bidOptions: BidOptionsOffer | null = data?.bid_options || null;

  // Local selections start from what the office set.
  const [selectedOptionals, setSelectedOptionals] = useState<Set<number> | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Map<number, number> | null>(null);
  const [chosenBidOptions, setChosenBidOptions] = useState<Set<number> | null>(null);
  if (data && selectedOptionals === null) {
    // The extras inside an option are ticked the same way as the bid's own, and go back in the same list.
    setSelectedOptionals(new Set<number>([
      ...lineItems.filter(li => li.is_optional && li.is_selected).map(li => li.id),
      ...optionalLinesOf(bidOptions).filter(li => Number(li.is_selected) === 1).map(li => li.id),
    ]));
    const optMap = new Map<number, number>();
    groups.forEach((g: any) => { const sel = (g.options || []).find((o: any) => o.is_selected); if (sel) optMap.set(g.id, sel.id); });
    setSelectedOptions(optMap);
    setChosenBidOptions(new Set<number>(bidOptions?.chosen || []));
  }

  const required = lineItems.filter(li => !li.is_optional);
  const optional = lineItems.filter(li => li.is_optional);
  // Mirror the server's recalcQuote: a line whose total column is empty still
  // counts as quantity × unit price. Without this a $495 line with a NULL
  // total showed the whole bid as $0 (Jess's overseed, Sep 28 2026).
  const lineTotal = (li: any) => Number(li.total) || (Number(li.quantity) || 0) * (Number(li.unit_price) || 0) || 0;
  const includedTotal = required.reduce((s, li) => s + lineTotal(li), 0);
  const addOnTotal = selectedOptionals ? optional.filter(li => selectedOptionals.has(li.id)).reduce((s, li) => s + lineTotal(li), 0) : 0;
  const chosenOptions = selectedOptions
    ? groups.map((g: any) => (g.options || []).find((o: any) => o.id === selectedOptions.get(g.id))).filter(Boolean)
    : [];
  const optionsTotal = chosenOptions.reduce((s: number, o: any) => s + (Number(o.price) || 0), 0);
  const addedByThem = bidDelta.monthly + bidDelta.visit + bidDelta.once;
  const winterOffer: WinterOffer | null = data?.winter || null;
  // One state for the winter card and the Now / In winter choice under each offered line (components/WinterChoice).
  const winterShown = !!winterOffer && !commercial;
  const winter = useWinterChoice(winterShown && !winterOffer!.locked ? winterOffer : null, {
    onChange: (pick, saving) => { setWinterPick(pick); setWinterSaving(saving); },
    price: async pick => { const d = (await publicApi.post(`/quotes/public/${token}/choices`, { winter: pick })).data?.winter; return d ? { saving: Number(d.saving) || 0, lines: d.lines || [] } : null; },
  });
  // The sizes a planting line is offered in (server: services/bushSizes). Null when no line on this bid offers any,
  // so a bid written before the picker draws exactly as it did.
  const sizeOffer: SizeOffer | null = data?.sizes || null;
  const sizes = useSizeChoice(sizeOffer && !sizeOffer.locked ? sizeOffer : null, {
    onChange: (pick, delta) => { setSizePick(pick); setSizeDelta(delta); },
    price: async pick => { const d = (await publicApi.post(`/quotes/public/${token}/choices`, { sizes: pick })).data?.sizes; return d ? { delta: Number(d.delta) || 0, lines: d.lines || [] } : null; },
  });
  // A line the office offered in sizes is drawn as the size picked right now: the words with no size on them (the
  // pills under it say which) and that size's price, so the row and the pills can never disagree. A line with no
  // sizes — every line on every older bid — is handed on exactly as it came.
  const sizeLine = (li: any) => {
    const l = sizes.lineFor(li.id);
    if (!l) return li;
    const s = l.sizes.find(x => x.key === (sizes.picks[String(li.id)] || l.chosen));
    return s ? { ...li, description: l.description, unit_price: s.price, total: s.total } : { ...li, description: l.description };
  };
  // The yard's site map on the bid (server: site_map, areas and notes only). Marks come as the server stored them.
  const siteMarks: SiteMark[] = React.useMemo(() => normalizeMarks(data?.site_map?.marks), [data?.site_map?.marks]);
  const sitePin: [number, number] | null = data?.site_map?.pin && Number.isFinite(Number(data.site_map.pin.lat)) && Number.isFinite(Number(data.site_map.pin.lng))
    ? [Number(data.site_map.pin.lat), Number(data.site_map.pin.lng)] : null;
  // The winter saving comes off the total: the server's number for their picks (or for the lines already in the window).
  const winterOff = winterOffer && !winterOffer.locked ? (winterSaving ?? Number(winterOffer.saving) ?? 0) : 0;
  // What the options they have ticked add (components/BidOptions — the same rule the server prices with).
  const bidOptionsTotal = optionsPrice(bidOptions, chosenBidOptions, selectedOptionals);
  // The size picks move the total by the server's own number (sizeDelta): the picked size's price less the price the
  // line is on now. Zero until they tap a different size, so the first paint is the bid as sent.
  const total = (data && selectedOptionals !== null && selectedOptions !== null ? includedTotal + addOnTotal + optionsTotal + bidOptionsTotal : (quote?.total || 0)) + addedByThem - winterOff + sizeDelta;
  // The bill in two: the winter lines at their winter price, and everything else (WinterChoice's split, the bar's words).
  const winterPart = winterOffer && !winterOffer.locked ? (winterOffer.lines || []).filter(l => winter.isWinter(l.id)).reduce((t, l) => t + Number(l.winter || 0), 0) : 0;
  const winterSplit = winterPart > 0 ? { now: Math.round((total - winterPart) * 100) / 100, winter: Math.round(winterPart * 100) / 100 } : null;
  const hasChoices = optional.length > 0 || groups.length > 0 || (bidOptions?.options?.length || 0) > 0;
  const per = (li: any) => li?.unit === 'month' ? '/mo' : li?.cadence === 'quarterly' ? ' a quarter' : li?.cadence === 'yearly' ? '/yr'
    : li?.unit === 'visit' ? '/visit' : li?.unit === 'year' ? '/yr' : '';
  // The lines of an option they took read like any other line from here on (the month / visit split, the one-time
  // total): a bid with no options adds nothing, so none of it changes.
  const takenOptionLines: any[] = (bidOptions?.options || [])
    .filter(o => chosenBidOptions?.has(o.id))
    .flatMap(o => o.sections.flatMap(sec => sec.lines))
    .filter(li => !Number(li.is_optional) || selectedOptionals?.has(li.id));
  const chosenLines = [...required, ...optional.filter(li => selectedOptionals?.has(li.id)), ...takenOptionLines];
  const monthlyTotal = chosenLines.filter(li => li.unit === 'month').reduce((s, li) => s + lineTotal(li), 0) + bidDelta.monthly;
  // Per-visit lines on their own cadence (a commercial bid's quarterly trim) are not the visit price.
  const offCadence = (li: any) => ['quarterly', 'monthly', 'yearly', 'one_time'].includes(String(li.cadence || ''));
  const visitTotal = chosenLines.filter(li => li.unit === 'visit' && !offCadence(li)).reduce((s, li) => s + lineTotal(li), 0) + bidDelta.visit;
  const quarterlyTotal = chosenLines.filter(li => li.unit === 'visit' && li.cadence === 'quarterly').reduce((s, li) => s + lineTotal(li), 0);
  const otherCadenceTotal = chosenLines.filter(li => li.unit === 'visit' && offCadence(li) && li.cadence !== 'quarterly').reduce((s, li) => s + lineTotal(li), 0);
  const oneTimeTotal = Math.max(0, total - monthlyTotal - visitTotal - quarterlyTotal - otherCadenceTotal);
  // Work billed per month on its own rhythm (server: services/monthlyLine — bush trimming $100 a month, every 3
  // months). Owner, Oct 27 2026, of the "most months / some months" box with it: "this is too confusing". That maths
  // belongs to a lawn plan on a mowing bid; a rhythm line is two numbers — the visit and the month — and what the
  // month is for. (A plan they add themselves brings the picture back, as before.)
  const rhythmLines = chosenLines.filter(li => li.unit === 'month' && li.rhythm_words);
  const rhythmOnly = rhythmLines.length > 0 && chosenLines.filter(li => li.unit === 'month').every(li => li.rhythm_words) && !(bidDelta.monthly > 0);
  const lc = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
  const rhythmWordsLines = rhythmLines.map(li => `${money(lineTotal(li))} a month for ${lc(String(li.description || '').split(/\s+[—–]\s+/)[0].trim())}, ${li.rhythm_words}`);
  const monthSentence = rhythmOnly
    ? `${rhythmLines.map(li => `${String(li.description || '').split(/\s+[—–]\s+/)[0].trim()} is ${money(lineTotal(li))} a month, ${li.rhythm_words}`).join('; ')} — on your month-end bill, the same every month.`
    : monthWords(quote?.service_frequency, visitTotal, money, monthlyTotal);
  const isRecurring = monthlyTotal > 0 || visitTotal > 0;
  const isMowingQuote = quote?.quote_kind === 'mowing' || (!quote?.quote_kind && !quote?.package_key && /\bmow/i.test(String(quote?.title || '')));
  // A plain mowing bid is one number: $55 a mow. The month picture (most months,
  // some months, the first-visit fee) belongs on a plan, not on top of a mow.
  const plainMow = isMowingQuote && !(monthlyTotal > 0) && ((Number(data?.mowing_offer?.per_mow) || 0) > 0 || visitTotal > 0);
  const mowAmount = (Number(data?.mowing_offer?.per_mow) || 0) > 0 ? Number(data.mowing_offer.per_mow) : visitTotal;
  // Done every visit besides the mow, each named with its own price — "$100 a mow" and "$50 a visit for leaf
  // raking", never "$150 a mow" (owner, Oct 27 2026). The server names them (recurringCredit.partsOf).
  const visitExtras: { label: string; amount: number }[] = Array.isArray(data?.mowing_offer?.extras) ? data.mowing_offer.extras : [];
  const extraWordsLines = visitExtras.map(e => `${money(e.amount)} a visit for ${lc(String(e.label || ''))}`);
  // Visit, month, most months, some months — all on the page, none tucked under a "plus".
  // A business reads plain prices — a visit, a quarter, one time — never the
  // homeowner's "most months / some months" picture (owner, Sep 29 2026).
  const picture = commercial || rhythmOnly ? null : monthPicture(quote?.service_frequency, visitTotal, monthlyTotal);
  // Every 4 weeks: the mow is a per-visit line on its own cadence, so it is in none of the sums above and the page
  // fell through to the bid's total "one time" — "$55.00 one time" right above "Then every 4 weeks" (review, Oct 6
  // 2026). It is the price of a mow, and the page says how often. Words only: the number is the line's own.
  const fourWeekMow = isMowingQuote && !commercial && quote?.service_frequency === 'monthly' && !(monthlyTotal > 0) && !(visitTotal > 0)
    ? chosenLines.filter(li => li.unit === 'visit' && String(li.cadence || '') === 'monthly').reduce((s, li) => s + lineTotal(li), 0) : 0;
  const headline = isContract
    ? { value: money(total), unit: 'a month' }
    : commercial && mowAmount > 0
    ? { value: money(mowAmount), unit: 'a visit' }
    : plainMow
    ? { value: money(mowAmount), unit: 'a mow' }
    : rhythmOnly && (mowAmount > 0 || visitTotal > 0)
    ? { value: money(mowAmount || visitTotal), unit: isMowingQuote && mowAmount > 0 ? 'a mow' : 'a visit' }
    : rhythmOnly
    ? { value: money(monthlyTotal), unit: 'a month' }
    : picture
    ? { value: money(picture.mostMonths), unit: picture.someMonths != null ? 'most months' : (monthlyTotal > 0 ? 'a month' : 'a visit') }
    : fourWeekMow > 0
    ? { value: money(fourWeekMow), unit: 'a mow, every 4 weeks' }
    : { value: money(total), unit: 'one time' };
  // A business's first visit carries its one-time work: "$420 this visit, then $220" (owner, Sep 30 2026),
  // and the quarterly work starts months later, not on every visit.
  const firstVisitDate: string | null = (() => {
    const so = data?.start_options;
    const opt = so?.options?.find((o: any) => o.key === (startChoice || so?.default)) || so?.options?.[0];
    return opt?.first || data?.booking?.first_visit || quote?.start_date || null;
  })();
  const qMonths = Number(data?.quarterly_first_months) || 4;
  const quarterlyFrom = firstVisitDate ? (() => {
    const d = new Date(`${String(firstVisitDate).slice(0, 10)}T12:00:00`); d.setMonth(d.getMonth() + qMonths);
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  })() : null;
  const quarterlyWhat = (() => {
    const names = chosenLines.filter(li => li.unit === 'visit' && li.cadence === 'quarterly')
      .map(li => String(li.description || '').replace(/^quarterly\s+/i, '').trim()).filter(Boolean);
    return names.length === 1 ? names[0].charAt(0).toLowerCase() + names[0].slice(1) : 'quarterly work';
  })();
  const quarterlyWords = quarterlyTotal > 0
    ? `${money(quarterlyTotal)} a quarter — ${quarterlyWhat}, ${quarterlyFrom ? `first in ${quarterlyFrom}` : `first ${qMonths} months after the first visit`}`
    : null;
  const firstVisitSplit = commercial && visitTotal > 0 && oneTimeTotal > 0;
  const priceLines = isContract ? [
    contract!.tax_pct > 0 ? `plus ${contract!.tax_pct}% sales tax` : null,
    `${money(total * contract!.months)} over ${contract!.months} months`,
  ].filter(Boolean) as string[] : plainMow ? extraWordsLines : [
    visitTotal > 0 && !['a visit', 'a mow'].includes(headline.unit) ? `${money(visitTotal)} a visit` : null,
    ...(rhythmOnly ? [...extraWordsLines, ...rhythmWordsLines] : [monthlyTotal > 0 && headline.unit !== 'a month' ? `${money(monthlyTotal)} a month` : null]),
    picture?.someMonths != null ? `${money(picture.someMonths)} some months` : null,
    quarterlyWords,
    oneTimeTotal > 0 && (monthlyTotal > 0 || visitTotal > 0 || fourWeekMow > 0) ? `${money(oneTimeTotal)} one time, on the first visit` : null,
  ].filter(Boolean) as string[];
  const depositBase = isRecurring ? oneTimeTotal : total;
  const scopeWithLiveTotal = React.useMemo(() => {
    const txt = String(quote?.scope_of_work || '');
    const base = Number(quote?.subtotal ?? includedTotal) || includedTotal;
    if (!txt || Math.abs(base - total) < 0.005) return txt;
    const forms = [money(base), `$${base.toFixed(2)}`, `$${base.toLocaleString('en-US')}`, `$${Math.round(base)}`];
    let out = txt;
    for (const f of Array.from(new Set(forms))) out = out.split(f).join(money(total));
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.scope_of_work, quote?.subtotal, includedTotal, total]);

  const respond = useMutation(
    async (action: 'accept' | 'decline') => {
      const r = await publicApi.post(`/quotes/public/${token}/respond`, {
        action,
        selected_item_ids: selectedOptionals ? Array.from(selectedOptionals) : [],
        selected_option_ids: selectedOptions ? Array.from(selectedOptions.values()) : [],
        // The options they ticked (server: services/bidOptions). Left out on a bid with none, so nothing changes there.
        selected_bid_option_ids: bidOptions ? Array.from(chosenBidOptions || []) : undefined,
        signed_name: signedName.trim() || undefined,
        code: code.trim() || undefined,
        referral_code: referral.trim() || undefined,
        start_choice: (data?.start_options?.options?.length || 0) > 1 ? (startChoice || data?.start_options?.default) : undefined,
        billing_choice: action === 'accept' && billingChoice?.offered ? billCadence : undefined,
        choices: action === 'accept' && ((bidChoices && (bidChoices.plan || bidChoices.addons.length || bidChoices.start || (bidChoices.shifts && bidChoices.shifts.length))) || winterPick || sizePick)
          ? { ...(bidChoices || { addons: [] }), ...(winterPick ? { winter: winterPick } : {}), ...(sizePick ? { sizes: sizePick } : {}) } : undefined,
      });
      return { action, referral: r.data?.referral || null, scheduled: r.data?.scheduled || null, winter: r.data?.winter || null, options: r.data?.options || null };
    },
    {
      onSuccess: ({ action, referral: ref, scheduled, winter, options }) => {
        setAcceptNote(ref?.message || null);
        if (options?.length) setTookOptions(options);
        if (winter) setWinterBooked(winter);
        if (scheduled?.start) setBooked(scheduled);
        setSheet(false);
        setDone(action === 'accept' ? 'accepted' : 'declined');
      },
      onError: (e: any) => {
        const d = e?.response?.data;
        if (d?.code_required && !verify) { sendCode.mutate(undefined); return; }
        // The office turned on a deposit after this page loaded: back to the page, where the button is now "Pay deposit".
        if (d?.deposit_required) { setSheet(false); setDepositError(d.message); refetch(); return; }
        setVerifyError(d?.message || 'That did not go through. Try again.');
      },
    }
  );
  // "Not right now" is no longer a decline on its own: "Remind me in a week" parks the follow-ups and keeps the bid
  // open (POST /quotes/public/:token/snooze); "I'm not interested" is the decline, on a second tap.
  const snooze = useMutation(async () => (await publicApi.post(`/quotes/public/${token}/snooze`)).data, {
    onSuccess: (d: any) => { setSheet(false); setVerifyError(null); setSnoozed(d?.message || "No problem — we'll check back with you in a week. Your bid stays open."); },
    onError: (e: any) => { setVerifyError(e?.response?.data?.message || 'That did not go through. Try again, or just text us.'); },
  });
  const sendCode = useMutation(
    async (via?: 'sms' | 'email') => (await publicApi.post(`/quotes/public/${token}/send-code`, via ? { via } : {})).data,
    {
      onSuccess: (d: any) => { setVerify({ to: d.to, via: d.via, options: d.options || {}, practice_code: d.practice_code || null }); setCode(''); setVerifyError(null); },
      onError: (e: any) => {
        const d = e?.response?.data;
        // Nowhere to send a code — or a server that predates codes (404): the
        // server accepts without one, so let them through.
        if (d?.no_contact || e?.response?.status === 404) { setVerify({ to: '', via: 'none' }); return; }
        setVerifyError(d?.message || 'Could not send the code. Try again in a moment.');
      },
    }
  );
  const confirmStart = useMutation(
    async (ok: boolean) => (await publicApi.post(`/quotes/public/${token}/start-confirm`, {
      ok,
      date: booked?.first_visit || data?.booking?.first_visit || booked?.start || quote?.start_date || undefined,
      note: ok ? undefined : (startNote.trim() || undefined),
      preferred: ok ? undefined : (startPreferred || undefined),
    })).data,
    {
      onSuccess: (d: any) => { setStartAsk('answered'); setStartThanks(d?.message || 'Thanks, we have that.'); },
      onError: () => { setStartAsk('answered'); setStartThanks('Text us and we will sort the day with you.'); },
    }
  );
  const card = data?.card;
  const needsCard = !!card?.required && !card?.on_file;
  // A card they may put on file themselves (owner, Oct 27 2026) — only when Stripe is set up, never the required flow.
  const cardOffered = !!card?.available && !card?.required;
  // "How would you like to be billed?" (services/billCadence): their pick, remembered on the bid the moment they tap it
  // so it survives the trip to Stripe's card form, and stamped on the customer when they sign.
  const billingChoice = data?.billing_choice;
  const [billPick, setBillPick] = useState<'per_visit' | 'month_end' | null>(null);
  const billCadence: 'per_visit' | 'month_end' = billPick || billingChoice?.cadence || 'month_end';
  const pickBilling = useMutation(
    (cadence: 'per_visit' | 'month_end') => publicApi.post(`/quotes/public/${token}/billing-choice`, { cadence, monthly: !!billingChoice?.monthly }).then(r => r.data),
    { onError: () => { /* the pick still rides along with accept */ } }
  );
  const saveCard = useMutation(
    () => publicApi.post(`/quotes/public/${token}/card`).then(r => r.data),
    {
      onSuccess: (d: any) => { if (d?.url) window.location.href = d.url; },
      onError: (e: any) => setVerifyError(e?.response?.data?.message || 'Could not open the card form.'),
    }
  );
  useEffect(() => {
    if (!token) return;
    const q = new URLSearchParams(window.location.search);
    if (q.get('card') !== 'saved') return;
    publicApi.post(`/quotes/public/${token}/card/confirm`).finally(() => {
      q.delete('card');
      const rest = q.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
      refetch();
    });
  }, [token, refetch]);

  // 50% upfront (owner, Sep 29 2026): paid by card before they can accept, off the final bill.
  const deposit = data?.deposit || null;
  const needsDeposit = !!deposit?.required && !deposit?.paid && !deposit?.billed && Number(deposit?.amount) > 0 && !!deposit?.collectable;
  // The deposit was taken on their picks: once it is paid they are locked in (text us to change them).
  const picksLocked = !!deposit?.paid;
  const [depositError, setDepositError] = useState<string | null>(null);
  const payDeposit = useMutation(
    () => publicApi.post(`/quotes/public/${token}/deposit`, {
      selected_item_ids: selectedOptionals ? Array.from(selectedOptionals) : [],
      selected_option_ids: selectedOptions ? Array.from(selectedOptions.values()) : [],
      // The deposit is a share of the options they took, not of the ones they left (services/bidOptions).
      selected_bid_option_ids: data?.bid_options ? Array.from(chosenBidOptions || []) : undefined,
    }).then(r => r.data),
    {
      onSuccess: (d: any) => { setDepositError(null); if (d?.url) window.location.href = d.url; else refetch(); },
      onError: (e: any) => { setDepositError(e?.response?.data?.message || 'Could not open the payment page — please call us.'); refetch(); },
    }
  );
  useEffect(() => {
    if (!token) return;
    const q = new URLSearchParams(window.location.search);
    const status = q.get('deposit');
    if (!status) return;
    const sid = q.get('session_id');
    const done = () => {
      q.delete('deposit'); q.delete('session_id');
      const rest = q.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
      refetch();
    };
    if (status === 'paid' && sid) publicApi.post(`/quotes/public/${token}/deposit/confirm`, { session_id: sid }).catch(() => {}).finally(done);
    else { if (status === 'cancelled') setDepositError('No payment was taken. Pay the deposit when you are ready to accept.'); done(); }
  }, [token, refetch]);

  function startAccept() {
    setVerifyError(null);
    if (verify && (verify.via === 'none' || code.trim().length === 6)) { respond.mutate('accept'); return; }
    if (!verify) { sendCode.mutate(undefined); return; }
  }

  if (isLoading) return <PublicLoading />;
  if (isError || !quote) return <DeadLink what="quote" company={co} />;

  // 'converted' and 'completed' are what an accepted bid becomes once the
  // office books the work — to the customer that is still "you're on the
  // schedule", never a fresh $0 bid with an Accept button.
  const alreadyResponded = ['accepted', 'declined', 'converted', 'completed'].includes(quote.status);
  const isPlanBid = String(quote.quote_kind || '') === 'treatment';
  // The thank-you screen says what was BOOKED: the reply to accept, or — when the page is opened again later — the
  // same thing read back off the calendar (data.booking). It used to print the first of the series as the start
  // and "then every Monday" whatever the frequency, and after a reload the day first agreed on the bid, whatever
  // was booked (owner, Oct 5 2026: "make sure on site routes work eg start days…"; the audit's finding #15).
  // A mowing bid with nothing booked yet names no date at all: Michael confirms it.
  const bk: any = booked?.first_visit ? booked : (data?.booking?.first_visit ? data.booking : null);
  const startDate: string | null = bk?.first_visit || (!isMowingQuote && quote.start_date ? String(quote.start_date).slice(0, 10) : null);
  // The time agreed belongs to the day agreed; a first mow on another day (they chose the route day) has no set time.
  const startTime = quote.start_time && (!bk || String(quote.start_date || '').slice(0, 10) === startDate) ? ` at ${clock(quote.start_time)}` : '';
  const askStart = !!startDate && !quote.start_reply;

  /* ── after they answer ─────────────────────────────────────────────── */
  if (done || alreadyResponded) {
    const isAccepted = done === 'accepted' || ['accepted', 'converted', 'completed'].includes(quote.status);
    // One filled button on this page at a time (owner, Oct 7 2026: "too many accept buttons"). A change waiting on
    // them is the only thing to answer — the day and the card come back once they have. After that the deposit is
    // the one to press, then the day, then the card on file; the others are outlined.
    const changeWaiting = !!data?.change;
    const depositToPay = isAccepted && !changeWaiting && !!deposit?.required && Number(deposit.amount) > 0 && !deposit.paid
      && !!deposit.collectable && !deposit.billed && ['accepted', 'converted'].includes(String(quote.status));
    const dayToAnswer = isAccepted && !changeWaiting && askStart && startAsk !== 'answered';
    const tookWords: string[] = tookOptions?.length ? tookOptions
      : (bidOptions?.options || []).filter(o => o.is_selected).map(o => `${o.title} (${money(Number(o.subtotal))})`);
    const DayYes = depositToPay ? ButtonGhost : ButtonPrimary;
    const CardButton = depositToPay || dayToAnswer ? ButtonGhost : ButtonPrimary;
    return (
      <PublicPage company={co}>
        <ChangeOrderPublic token={token} title={quote.title} data={data} onChanged={refetch} />
        <Panel tone={isAccepted ? 'good' : 'plain'} className="text-center">
          {isAccepted ? <CheckCircle className="h-12 w-12 text-emerald-600 mx-auto mb-3" aria-hidden />
            : <XCircle className="h-12 w-12 text-gray-500 mx-auto mb-3" aria-hidden />}
          <H2>{isAccepted ? (isPlanBid ? "You're on the plan" : "You're on the schedule") : 'Quote declined'}</H2>
          {isAccepted ? (
            <p className="text-[15px] text-gray-800 mt-3">
              Thank you, {quote.customer_name}.{' '}
              {startDate
                ? (bk?.one_time
                  ? <>We have you down for <span className="font-bold">{longDate(startDate)}{startTime}</span>.</>
                  : bk && bk.is_start === false
                    ? <>Your next mow is <span className="font-bold">{longDate(startDate)}</span>{bk.then_words ? `, ${bk.then_words}` : ''}.</>
                    : <>We have you down to start <span className="font-bold">{longDate(startDate)}{startTime}</span>{bk?.then_words ? `, ${bk.then_words}` : ''}.</>)
                : isPlanBid ? <>Your first treatment is booked and we will text you before we come out.</> : <>Michael will text you to confirm a start date.</>}
            </p>
          ) : (
            <p className="text-[15px] text-gray-800 mt-3">
              Thanks for letting us know. If anything changes we are a text away at {co.phone}.
            </p>
          )}
        </Panel>
        <ChangesDone data={data} />

        {isAccepted && !changeWaiting && askStart && startAsk === 'idle' && (
          <Panel title="Does that day work?">
            <div className="grid grid-cols-2 gap-2">
              <DayYes onClick={() => confirmStart.mutate(true)} disabled={confirmStart.isLoading}>That works</DayYes>
              <ButtonGhost onClick={() => setStartAsk('form')} disabled={confirmStart.isLoading}>Another day</ButtonGhost>
            </div>
          </Panel>
        )}
        {isAccepted && !changeWaiting && askStart && startAsk === 'form' && (
          <Panel title="When would suit you better?">
            <label className="block text-sm font-semibold text-gray-800">The day you would prefer
              <input type="date" value={startPreferred} onChange={e => setStartPreferred(e.target.value)}
                className="mt-1 w-full border-2 border-brand-200 rounded-2xl px-3 min-h-[52px] text-[16px]" />
            </label>
            <label className="block text-sm font-semibold text-gray-800 mt-3">Anything we should know
              <textarea value={startNote} onChange={e => setStartNote(e.target.value)} rows={2} placeholder="The dog, the gate, a day that never works"
                className="mt-1 w-full border-2 border-brand-200 rounded-2xl px-3 py-2 text-[16px]" />
            </label>
            <div className="mt-3">
              <ButtonPrimary onClick={() => confirmStart.mutate(false)} disabled={confirmStart.isLoading}>
                {confirmStart.isLoading ? 'Sending' : 'Send it to Michael'}
              </ButtonPrimary>
            </div>
          </Panel>
        )}
        {isAccepted && startAsk === 'answered' && startThanks && (
          <Panel tone="brand"><p className="text-[15px] text-gray-900">{startThanks}</p></Panel>
        )}
        {/* The winter job off this bid (services/winterWork): booked just now, or read back when the page is opened later. */}
        {isAccepted && (winterBooked?.words || winterOffer?.booked?.words) && (
          <Panel tone="brand"><p className="text-[15px] text-gray-900">{winterBooked?.words || winterOffer?.booked?.words}</p></Panel>
        )}
        {/* Several options on one bid (services/bidOptions): the ones they took, said back to them — the accept's own
            words, or read off the bid when the page is opened again later. */}
        {isAccepted && tookWords.length > 0 && (
          <Panel tone="brand"><p className="text-[15px] text-gray-900">You chose {tookWords.join(' and ')}.</p></Panel>
        )}
        {isAccepted && acceptNote && <Panel tone="brand"><p className="text-[15px] text-gray-900">{acceptNote}</p></Panel>}
        {isAccepted && deposit?.required && Number(deposit.amount) > 0 && (
          <Panel tone={deposit.paid ? 'brand' : 'warn'}>
            {deposit.paid
              ? <p className="text-[15px] text-gray-900">Deposit {money(Number(deposit.paid_amount))} paid. It comes off your final bill.</p>
              : <>
                  {/* A change is waiting on them: that first — the deposit is 50% of the total they end up agreeing to. */}
                  {data?.change
                    ? <p className="text-[15px] text-gray-900">{deposit.pct}% deposit due before we start. Answer the change above first — your deposit is taken on the new total. It comes off your final bill.</p>
                    : <p className="text-[15px] text-gray-900">{deposit.pct}% deposit due before we start: <span className="font-bold">{money(Number(deposit.amount))}</span>. It comes off your final bill.</p>}
                  {depositToPay && (
                    <div className="mt-3">
                      <ButtonPrimary onClick={() => payDeposit.mutate()} disabled={payDeposit.isLoading}>{payDeposit.isLoading ? 'Opening…' : `Pay ${money(Number(deposit.amount))} deposit`}</ButtonPrimary>
                    </div>
                  )}
                </>}
            {depositError && <p className="text-[14px] font-semibold text-red-700 mt-2">{depositError}</p>}
          </Panel>
        )}
        {isAccepted && !changeWaiting && data?.portal_url && (
          <>
            <CardButton href={`${data.portal_url}?tab=settings`}>Set up payment</CardButton>
            <p className="text-[13px] text-gray-600 text-center">
              {billingChoice?.offered && billingChoice?.picked ? `Billed ${String(billingChoice.words).toLowerCase()}. ${billingChoice.sentence}` : (data?.billing || BILLING_SENTENCE)}
            </p>
          </>
        )}
        {/* The free lawn walkthrough (owner, Oct 20 2026) — outlined, under everything that pays or books. */}
        {!commercial && <LawnWalkthroughButton kind="bid" token={token!} />}
      </PublicPage>
    );
  }

  const quoteNo = `Q-${String(quote.id).padStart(4, '0')}`;
  // Nothing to accept yet: a draft, or no priced work on it. A lawn-plan bid is the three plans themselves:
  // it is ready as soon as it is sent, even before they tap a card (The Plan is the default).
  const notReady = isPlanBid
    ? quote.status === 'draft'
    // A bid whose work is all inside its options reads $0 until they tick one — it is ready, they just have not
    // picked yet.
    : quote.status === 'draft' || (lineItems.length === 0 && !bidOptions)
      || (!(total > 0) && !(bidOptions?.options || []).some(o => Number(o.subtotal) > 0));
  // A bid with options is not accepted with none of them ticked — the server refuses it, and the button says so
  // before they get that far (server: services/bidOptions.acceptRefusal).
  const needsAnOption = !!bidOptions && !bidOptions.locked && !(chosenBidOptions?.size);
  const overgrown = lineItems
    .filter((li: any) => li.is_selected && !li.unit && !li.is_optional && /overgrow|catch-?up|first cut|tall grass/i.test(String(li.description)))
    .reduce((s: number, li: any) => s + (Number(li.unit_price) || Number(li.total) || 0), 0);
  // Opened again while their "remind me in a week" still runs: the same note, off the bid itself (a stored UTC stamp).
  const snoozeUntil = quote.snoozed_until ? new Date(`${String(quote.snoozed_until).replace(' ', 'T').slice(0, 19)}Z`) : null;
  const snoozeNote = snoozeUntil && snoozeUntil.getTime() > Date.now()
    ? `You asked us to check back around ${longDate(snoozeUntil.toLocaleDateString('en-CA', { timeZone: 'America/Chicago' }))}. Your bid stays open — accept it here any time.` : null;
  const canAccept = !respond.isLoading && !sendCode.isLoading && !snooze.isLoading
    && !needsAnOption
    && !!signedName.trim()
    && !(!!verify && verify.via !== 'none' && code.trim().length !== 6)
    && !(isPlanBid && !bidChoices?.plan && !(monthlyTotal > 0));
  const extras = !notReady && data?.page?.extras ? (
    <Panel>
      <BidExtras extras={data.page.extras} onChange={(c, d) => { setBidChoices(c); setBidDelta(d); }}
        price={async choice => (await publicApi.post(`/quotes/public/${token}/choices`, choice)).data?.plan || null} />
    </Panel>
  ) : null;

  return (
    <PublicPage company={co} context={`Quote ${quoteNo}`}>
      <style>{`@media print { .no-print { display: none !important; } }`}</style>

      {/* What this is, who it is for, what it costs — above the fold. A business reads a proposal (BusinessProposal):
          letterhead, the price as tiles, the year, the three promises. The maths is the same as below. */}
      {commercial ? (
        <BusinessProposal facts={commercial} title={quote.title} quoteNo={quoteNo} createdAt={quote.created_at}
          frequency={quote.service_frequency} visitTotal={visitTotal} monthlyTotal={monthlyTotal} quarterlyTotal={quarterlyTotal}
          otherCadenceTotal={otherCadenceTotal} oneTimeTotal={oneTimeTotal} quarterlyWords={quarterlyWords}
          firstVisit={data?.mowing_offer ? null : firstVisitDate}
          firstVisitWords={data?.mowing_offer || !data?.start_options?.options?.length ? null : (() => {
            const so = data.start_options;
            const o = so.options.find((x: any) => x.key === (startChoice || so.default)) || so.options[0];
            return `${so.frequency === 'one_time' ? 'Your visit is' : 'First visit'} ${longDay(o.first)}. ${thenLine(o, so.every)}`;
          })()}
          contract={isContract ? { monthly: total, months: contract!.months, tax_pct: contract!.tax_pct, start: contract!.start } : null}
          signedName={quote.agreement_name} signedAt={quote.agreement_signed_at} />
      ) : (
      <section className="bg-white rounded-3xl border border-brand-100 shadow-lift overflow-hidden">
        <div className="px-5 pt-5">
          <p className="text-[13px] font-black uppercase tracking-wide text-brand-600">Your quote</p>
          <H1 className="mt-1">{quote.title}</H1>
          <p className="text-[15px] text-gray-700 mt-2">
            For <span className="font-semibold text-gray-900">{quote.customer_name}</span>
            {quote.address ? <> at {quote.address}{quote.city ? `, ${quote.city}` : ''}</> : null}
          </p>
        </div>
        {!plainMow && (
        <div className="px-5 py-6 mt-4 bg-brand-600 text-black">
          <p className="text-[13px] font-black uppercase tracking-wide text-black/70">Your price</p>
          {(picture || commercial) && (visitTotal > 0 || monthlyTotal > 0) ? (
            <>
              <div className={`mt-3 grid gap-4 ${(visitTotal > 0 && monthlyTotal > 0) ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {firstVisitSplit ? (
                  // One big number: what this first visit costs (the visit + its one-time work); the rest underneath.
                  <div>
                    <p className="text-[13px] font-black uppercase tracking-wide text-black/70">This visit</p>
                    <p className="text-4xl sm:text-5xl font-black tracking-tight tabular-nums">{money(visitTotal + oneTimeTotal)}</p>
                    <p className="text-[14px] font-semibold text-black/80 mt-1">{money(visitTotal)} visit + {money(oneTimeTotal)} one-time work</p>
                    <p className="text-[17px] font-bold mt-3">Then {money(visitTotal)} a visit</p>
                  </div>
                ) : visitTotal > 0 && (
                  <div>
                    <p className="text-[13px] font-black uppercase tracking-wide text-black/70">A visit</p>
                    <p className="text-3xl sm:text-4xl font-black tracking-tight tabular-nums">{money(visitTotal)}</p>
                  </div>
                )}
                {monthlyTotal > 0 && (
                  <div>
                    <p className="text-[13px] font-black uppercase tracking-wide text-black/70">A month</p>
                    <p className="text-3xl sm:text-4xl font-black tracking-tight tabular-nums">{money(monthlyTotal)}</p>
                  </div>
                )}
              </div>
              {picture && picture.someMonths != null && picture.usualVisits != null && picture.someVisits != null && (
                <div className="mt-5 pt-4 border-t border-black/20 space-y-3">
                  <div>
                    <p className="text-[13px] font-black uppercase tracking-wide text-black/70">Most months</p>
                    <p className="text-4xl font-black tracking-tight tabular-nums">{money(picture.mostMonths)}</p>
                    <p className="text-[15px] font-semibold text-black/85 mt-1">
                      {picture.usualVisits} visits{monthlyTotal > 0 ? ` plus ${money(monthlyTotal)} a month` : ''}
                    </p>
                  </div>
                  <div>
                    <p className="text-[13px] font-black uppercase tracking-wide text-black/70">Some months</p>
                    <p className="text-3xl font-black tracking-tight tabular-nums">{money(picture.someMonths)}</p>
                    <p className="text-[15px] font-semibold text-black/85 mt-1">
                      {picture.someVisits} visits instead of {picture.usualVisits}
                      {monthlyTotal > 0 ? `, plus ${money(monthlyTotal)} a month` : ''}
                    </p>
                  </div>
                </div>
              )}
              {quarterlyWords && (
                <p className="text-[17px] font-bold mt-4">{quarterlyWords}</p>
              )}
              {oneTimeTotal > 0 && !firstVisitSplit && (
                <p className="text-[17px] font-bold mt-4">{money(oneTimeTotal)} one time, on the first visit</p>
              )}
            </>
          ) : (
            <>
              <p className="mt-1 flex items-baseline gap-2 flex-wrap">
                <span className="text-4xl font-black tracking-tight tabular-nums">{headline.value}</span>
                <span className="text-lg font-semibold text-black/75">{headline.unit}</span>
              </p>
              {rhythmOnly && [...extraWordsLines, ...rhythmWordsLines].map(w => (
                <p key={w} className="text-[17px] font-bold mt-3">{headline.unit === 'a month' ? w.replace(/^\S+ a month /, 'For ') : `Plus ${w}`}</p>
              ))}
              {(fourWeekMow > 0 || rhythmOnly) && oneTimeTotal > 0 && (
                <p className="text-[17px] font-bold mt-4">{money(oneTimeTotal)} one time, on the first visit</p>
              )}
            </>
          )}
          {/* The dates are the server's — the ones accept books (start_options). The start card under the price
              (MowingOffer) shows them when the bid has one; an every-4-weeks or one-time mow has no such card, so its
              date is said here. No date is worked out in the browser any more: "then every other Monday" used to be
              guessed from the weekday, which knows nothing about which week the route is out. */}
          {data?.start_options?.options?.length ? (data?.mowing_offer ? null : (() => {
            const so = data.start_options;
            const o = so.options.find((x: any) => x.key === (startChoice || so.default)) || so.options[0];
            const at = quote.start_time && String(quote.start_date || '').slice(0, 10) === o.first ? ` at ${clock(quote.start_time)}` : '';
            return (
              <p className="text-[15px] font-medium text-black/80 mt-3">
                {so.frequency === 'one_time' ? 'Your mow is' : 'First mow'} {longDay(o.first)}{at}. {thenLine(o, so.every)}
              </p>
            );
          })()) : data?.booking?.first_visit ? (
            <p className="text-[15px] font-medium text-black/80 mt-3">
              Your next mow is {longDay(data.booking.first_visit)}.
              {data.booking.next_visit ? ` After that${data.booking.every ? `, ${data.booking.every}` : ''}, from ${longDay(data.booking.next_visit)}.` : ''}
            </p>
          ) : quote.start_date && (
            <p className="text-[15px] font-medium text-black/80 mt-3">
              First visit {longDay(quote.start_date)}{quote.start_time ? ` at ${clock(quote.start_time)}` : ''}.
            </p>
          )}
        </div>
        )}
      </section>
      )}

      {/* A lawn-plan bid: the three plans are the bid, so they come first. */}
      {isPlanBid && extras}

      {/* The price as a picture: one-time first, then recurring, the credit after three mows, and the first-mow date. */}
      {data?.mowing_offer && (
        <MowingOffer offer={data.mowing_offer} start={data.start_options} choice={startChoice} onChoice={setStartChoice}
          commercial={!!commercial}
          locked={done !== null || !['draft', 'sent'].includes(String(quote.status))} />
      )}

      {overgrown > 0 && !data?.mowing_offer && (
        <Panel tone="warn">
          <H2>Your lawn is overgrown right now</H2>
          <p className="text-[15px] text-gray-900 mt-2">
            The first visit is <b>{money(visitTotal + overgrown)}</b>: the {money(visitTotal)} mow plus a one-time{' '}
            <b>{money(overgrown)} catch-up</b> for the extra passes and the time. After that it is {money(visitTotal)} a visit.
            The catch-up never repeats unless the lawn is let go again.
          </p>
        </Panel>
      )}

      {/* The job scope written from this bid's lines (server/services/bidScope). */}
      {data?.job_scope && (
        <Panel title={isRecurring ? 'What we do each visit' : "What we'll do"}>
          {data.job_scope.headline && <p className="text-[15px] text-gray-900 leading-relaxed">{data.job_scope.headline}</p>}
          <div className="mt-3 space-y-3">
            {(data.job_scope.work || []).filter((w: any) => (w.steps || []).length > 0).map((w: any, i: number) => (
              <div key={i}>
                <p className="text-[14px] font-bold text-gray-900">{w.line}{w.optional ? <span className="font-normal text-gray-600"> · only if you add it</span> : null}</p>
                <ul className="mt-1 space-y-1 list-disc pl-5 text-[14px] text-gray-800 leading-snug">
                  {w.steps.map((st: string, j: number) => <li key={j}>{st}</li>)}
                </ul>
              </div>
            ))}
            {(data.job_scope.materials || []).length > 0 && (
              <div>
                <p className="text-[14px] font-bold text-gray-900">Materials we bring</p>
                <ul className="mt-1 space-y-1 list-disc pl-5 text-[14px] text-gray-800">{data.job_scope.materials.map((m: string, j: number) => <li key={j}>{m}</li>)}</ul>
              </div>
            )}
            {(data.job_scope.customer_prep || []).length > 0 && (
              <div>
                <p className="text-[14px] font-bold text-gray-900">Before we come</p>
                <ul className="mt-1 space-y-1 list-disc pl-5 text-[14px] text-gray-800">{data.job_scope.customer_prep.map((m: string, j: number) => <li key={j}>{m}</li>)}</ul>
              </div>
            )}
            {(data.job_scope.not_included || []).length > 0 && (
              <p className="text-[13px] text-gray-600">Not included: {data.job_scope.not_included.join('; ')}.</p>
            )}
          </div>
          <p className="text-[13px] text-gray-600 mt-3">{data?.billing || BILLING_SENTENCE}</p>
        </Panel>
      )}

      {/* "Every visit" only on work that repeats. A one-time job used to wear
          the mowing heading (owner, Sep 24 2026: "project says what we do every visit"). */}
      {!data?.job_scope && (quote.intro_note || isRecurring) && (
        <Panel title={commercial ? `Your service${commercial.site ? ` at ${commercial.site}` : ''}` : isRecurring ? 'What we do every visit' : 'About this job'}>
          <p className="text-[15px] text-gray-800 leading-relaxed">
            {quote.intro_note || [
              isMowingQuote ? 'Every visit we mow, edge, string trim, and blow off the driveway, walks and patio.' : '',
              monthSentence,
              data?.billing || BILLING_SENTENCE,
            ].filter(Boolean).join(' ')}
          </p>
          {data?.tax && (
            <p className="text-[13px] text-gray-600 mt-2">
              {data.tax.charged ? 'Tax is its own line below.'
                : isContract && contract!.tax_pct > 0 ? `Texas sales tax (${contract!.tax_pct}%) is added to each monthly bill.`
                : `Tax is ${data.tax.note}. Nothing is added after you accept.`}
            </p>
          )}
        </Panel>
      )}

      {media.some((m: any) => String(m.mime_type || 'image/jpeg').startsWith('image/')) && (
        <Panel title={commercial ? 'Your property' : 'Your yard'}>
          <div className="grid grid-cols-2 gap-2">
            {media.filter((m: any) => String(m.mime_type || 'image/jpeg').startsWith('image/')).map((m: any) => <img key={m.id} src={m.data} alt={m.file_name || 'Your yard'} className="w-full h-32 object-cover rounded-2xl" />)}
          </div>
        </Panel>
      )}

      {media.some((m: any) => !String(m.mime_type || 'image/jpeg').startsWith('image/')) && (
        <Panel title="Papers">
          <ul className="space-y-2">
            {media.filter((m: any) => !String(m.mime_type || 'image/jpeg').startsWith('image/')).map((m: any) => (
              <li key={m.id}>
                <a href={m.data} download={m.file_name || 'file.pdf'} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[15px] font-semibold text-gray-900">
                  <FileText className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{m.file_name || 'Document'}</span>
                </a>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {/* "Do it in winter, save 10%" — the deal in words; the choice itself is on each offered line below (WinterChoice). */}
      {winterOffer && winterShown && !isPlanBid && !notReady && <WinterChoice offer={winterOffer} state={winter} split={winterSplit} />}

      {/* "Choose your plant size" — the choice itself is on each planting line below (SizeChoice). */}
      {sizeOffer && !notReady && <SizeChoice offer={sizeOffer} state={sizes} />}

      {/* Several options on one bid (components/BidOptions): each one its own title, photos and priced lines, and
          theirs to tick. Above the bid's own lines, because this is the choice the page is asking them to make.
          A size choice lives on the LINE, so the picker hangs under an option's lines too — but only once the
          option is taken, so a size tapped on an option nobody wants cannot move the total. */}
      {bidOptions && !notReady && chosenBidOptions !== null && (
        <BidOptions offer={bidOptions} chosen={chosenBidOptions} ticked={selectedOptionals}
          onToggle={id => { if (!picksLocked) setChosenBidOptions(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); }}
          onToggleLine={id => { if (!picksLocked) setSelectedOptionals(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); }}
          extra={(li, on) => (on && sizeOffer && !notReady ? <SizeLinePick state={sizes} id={li.id} /> : null)} />
      )}

      {/* On a lawn-plan bid the three cards above ARE the quote, so there is no line list. Tap a line to read what it is. */}
      {!isPlanBid && required.length > 0 && (
        <Panel title={bidOptions ? 'With any of them' : "What's included"}>
          <BidLines lines={required.map(li => sizeLine(li))} info={data?.page?.line_info} how={data?.page?.extras?.plan?.how_a_visit_works}
            extra={li => (
              <>
                {sizeOffer && !notReady && <SizeLinePick state={sizes} id={li.id} />}
                {winter.offer && !notReady ? <WinterLinePick state={winter} id={li.id} /> : null}
              </>
            )} />
        </Panel>
      )}

      {groups.length > 0 && selectedOptions !== null && groups.map((g: any) => (
        <Panel key={g.id} title={g.name}>
          {g.description && <p className="text-[14px] text-gray-700 -mt-2 mb-3">{g.description}</p>}
          <ul className="space-y-2">
            {(g.options || []).map((opt: any) => {
              const chosen = selectedOptions.get(g.id) === opt.id;
              return (
                <li key={opt.id}>
                  <OptionRow on={chosen} ariaLabel={opt.name} onClick={() => { if (!picksLocked) setSelectedOptions(prev => new Map(prev).set(g.id, opt.id)); }}>
                    <span className={`h-6 w-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${chosen ? 'border-brand-600 bg-white' : 'border-brand-300'}`}>
                      {chosen && <span className="h-3 w-3 rounded-full bg-brand-600" />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[15px] font-bold text-gray-900">{opt.name}</span>
                      {opt.description && <span className="block text-[14px] text-gray-700 mt-0.5 leading-snug">{opt.description}</span>}
                    </span>
                    <span className="text-[15px] font-bold text-gray-900 tabular-nums whitespace-nowrap">
                      {Number(opt.price) > 0 ? `+${money(opt.price)}${isContract ? '/mo' : ''}` : isContract ? 'No charge' : 'Included'}
                    </span>
                  </OptionRow>
                </li>
              );
            })}
          </ul>
        </Panel>
      ))}

      {optional.length > 0 && selectedOptionals !== null && (
        <Panel title={isContract ? 'Extras' : 'Add anything you like'}>
          <p className="text-[14px] text-gray-700 -mt-2 mb-3">Tap one to add it. The {isContract ? 'month' : 'price'} at the bottom keeps up.</p>
          <ul className="space-y-2">
            {optional.map((li: any) => {
              const on = selectedOptionals.has(li.id);
              return (
                <li key={li.id}>
                  <OptionRow on={on} ariaLabel={li.description}
                    onClick={() => { if (!picksLocked) { if (on) winter.setLine(li.id, false); setSelectedOptionals(prev => { const n = new Set(prev); if (n.has(li.id)) n.delete(li.id); else n.add(li.id); return n; }); } }}>
                    <span className={`h-6 w-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 ${on ? 'bg-brand-600 border-brand-600' : 'border-brand-300'}`}>
                      {on && <Check className="h-4 w-4 text-white" aria-hidden />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[15px] font-bold text-gray-900">{li.description}</span>
                      {Number(li.quote_on_request)
                        ? <span className="block text-[13px] text-gray-600">Tick it and we'll send you a price. Nothing is added to the total.</span>
                        : Number(li.quantity) !== 1 && <span className="block text-[13px] text-gray-600 tabular-nums">{li.quantity} × {money(li.unit_price)}</span>}
                    </span>
                    <span className={`text-[15px] font-bold tabular-nums whitespace-nowrap ${on ? 'text-brand-700' : 'text-gray-600'}`}>
                      {Number(li.quote_on_request) ? 'Quote on request' : <>+{money(li.total)}<span className="font-semibold">{per(li)}</span></>}
                    </span>
                  </OptionRow>
                  {/* Ticked, and offered in sizes: which plant they want (SizeLinePick). */}
                  {on && sizeOffer && !notReady && <SizeLinePick state={sizes} id={li.id} />}
                  {/* Ticked, and offered in winter: when they want it done (WinterLinePick). */}
                  {on && winter.offer && !notReady && <WinterLinePick state={winter} id={li.id} />}
                </li>
              );
            })}
          </ul>
        </Panel>
      )}

      {/* The yard's site map, when the office put it on the bid (owner, Oct 27 2026: "so it shows where the work gets
          done… just for some lawns that are bigger"). Areas and notes only — the server never sends a lock, a gate or a
          code. The map does not pan: a thumb over it scrolls the page. No button here, on purpose. */}
      {siteMarks.length > 0 && (
        <Panel title="Where the work gets done">
          <div className="rounded-2xl overflow-hidden border border-brand-100 mb-3">
            <SiteMapView marks={siteMarks} pin={sitePin} pannable={false} className="h-72" />
          </div>
          <ul className="space-y-2">
            {siteMarks.filter(m => m.kind === 'area').map((a, i) => (
              <li key={a.id ?? `a${i}`} className="flex items-start gap-2.5">
                <span className="mt-1.5 h-3 w-3 rounded-full flex-shrink-0 border border-black/20" style={{ background: markColor(a) }} aria-hidden />
                <div className="min-w-0">
                  <p className="text-[15px] font-bold text-gray-900 leading-snug">{a.label || 'Area'}{a.work ? <span className="font-semibold text-gray-700"> — {a.work}</span> : null}</p>
                  {a.notes && <p className="text-[14px] text-gray-700">{a.notes}</p>}
                </div>
              </li>
            ))}
            {siteMarks.filter(m => m.kind === 'note').map((n, i) => (
              <li key={n.id ?? `n${i}`} className="flex items-start gap-2.5">
                <span className="text-gray-500 flex-shrink-0" aria-hidden>✎</span>
                <p className="text-[14px] text-gray-800"><span className="font-bold">{n.label || 'Note'}</span>{n.notes ? ` — ${n.notes}` : ''}</p>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {deposit?.required && Number(deposit.amount) > 0 && (
        <Panel title="Deposit" tone={needsDeposit ? 'warn' : 'brand'}>
          {deposit.paid ? (
            <>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[15px] text-gray-800">Deposit paid · thank you</span>
                <span className="text-[15px] font-bold text-gray-900 tabular-nums">{money(Number(deposit.paid_amount))}</span>
              </div>
              <p className="text-[14px] text-gray-700 mt-1">It comes off your final bill. Your picks are locked in with it — text us to change them.{!alreadyResponded ? ' Accept below to book the work.' : ''}</p>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[15px] text-gray-800">{deposit.pct}% upfront, before we start</span>
                <span className="text-[15px] font-bold text-gray-900 tabular-nums">{money(Number(deposit.amount))}</span>
              </div>
              <p className="text-[14px] text-gray-700 mt-1">
                {deposit.collectable ? 'Paid by card when you accept. ' : 'We will send you a way to pay it. '}
                The rest, {money(Math.max(0, Number(deposit.base) - Number(deposit.amount)))}, is on the final bill.
              </p>
            </>
          )}
          {depositError && <p className="text-[14px] font-semibold text-red-700 mt-2">{depositError}</p>}
        </Panel>
      )}
      {!deposit?.required && Number(quote.deposit_pct) > 0 && depositBase > 0 && (
        <Panel title="Deposit">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[15px] text-gray-800">Due before we start ({Number(quote.deposit_pct)}%)</span>
            <span className="text-[15px] font-bold text-gray-900 tabular-nums">{money(depositBase * Number(quote.deposit_pct) / 100)}</span>
          </div>
          <p className="text-[14px] text-gray-700 mt-1">Balance {money(depositBase - depositBase * Number(quote.deposit_pct) / 100)} on completion.</p>
        </Panel>
      )}

      {quote.scope_of_work && (
        <section className="bg-white rounded-3xl border border-brand-100 shadow-soft overflow-hidden">
          <button type="button" onClick={() => setScopeOpen(v => !v)} aria-expanded={scopeOpen}
            className="press w-full flex items-center justify-between gap-3 px-5 py-4 text-left min-h-[60px]">
            <span className="text-[15px] font-black uppercase tracking-tight text-gray-900">Read the full agreement</span>
            <ChevronDown className={`h-5 w-5 text-gray-700 flex-shrink-0 transition-transform ${scopeOpen ? 'rotate-180' : ''}`} aria-hidden />
          </button>
          {scopeOpen && <pre className="px-5 pb-5 text-[14px] text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">{scopeWithLiveTotal}</pre>}
        </section>
      )}

      {/* What they may add themselves: bed weed spraying, a lawn plan or a bigger one, an extra application.
          Never asks the lawn's size or the grass; the server priced it for this yard. */}
      {!isPlanBid && extras}

      <div className="flex items-center justify-between no-print pt-1">
        <button type="button" onClick={copyPageLink}
          className="press inline-flex items-center gap-1.5 text-[14px] font-bold text-brand-700 min-h-[48px] px-1">
          {pageCopied ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />} {pageCopied ? 'Link copied' : 'Copy link'}
        </button>
        <button type="button" onClick={() => window.print()}
          className="press inline-flex items-center gap-1.5 text-[14px] font-bold text-brand-700 min-h-[48px] px-1">
          <Printer className="h-4 w-4" aria-hidden /> Print
        </button>
      </div>

      {/* Billed after each visit, or at month end — one small choice before they sign (owner, Oct 27 2026). Anything
          by the month (a lawn plan, a $/month line) is always on the month-end bill, and the page says so. */}
      {billingChoice?.offered && !alreadyResponded && !notReady && (
        <Panel title="How would you like to be billed?">
          <div className="space-y-2" role="radiogroup" aria-label="How would you like to be billed?">
            {([['per_visit', 'After each visit', 'A bill for each visit, the day it is done.'],
               ['month_end', 'At month end', 'One bill for the visits done that month.']] as const).map(([key, label, sub]) => (
              <OptionRow key={key} on={billCadence === key} ariaLabel={label}
                onClick={() => { setBillPick(key); pickBilling.mutate(key); }}>
                <span className={`h-5 w-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${billCadence === key ? 'border-brand-600 bg-brand-600' : 'border-brand-300 bg-white'}`} aria-hidden>
                  {billCadence === key && <Check className="h-3.5 w-3.5 text-white" />}
                </span>
                <span className="min-w-0">
                  <span className="block text-[15px] font-bold text-gray-900">{label}</span>
                  <span className="block text-[13px] text-gray-600">{sub}</span>
                </span>
              </OptionRow>
            ))}
          </div>
          {billingChoice.monthly && (
            <p className="text-[13px] text-gray-600 mt-2">Anything billed by the month, like a lawn plan, is always on the month-end bill.</p>
          )}
        </Panel>
      )}

      {card?.required && !alreadyResponded && (
        <Panel tone={needsCard ? 'warn' : 'brand'}>
          <p className="text-[15px] text-gray-900">
            {needsCard
              ? 'A card on file is required before you can accept. Nothing is charged now.'
              : `Card on file${card.brand ? ` · ${card.brand}` : ''}${card.last4 ? ` ···· ${card.last4}` : ''}.${deposit?.required ? '' : ' Nothing is charged by accepting.'}`}
          </p>
        </Panel>
      )}
      {/* Their own choice to put a card on file (owner, Oct 27 2026): outlined — the filled button stays "Sign and accept". */}
      {cardOffered && !alreadyResponded && !notReady && (
        card.on_file ? (
          <p className="text-[14px] text-gray-800 text-center">
            <span className="font-bold">{brandWord(card.brand)}{card.last4 ? ` ···· ${card.last4}` : ''}</span> on file. Nothing is charged by accepting.
          </p>
        ) : (
          <div>
            <ButtonGhost onClick={() => saveCard.mutate()} disabled={saveCard.isLoading}>
              {saveCard.isLoading ? 'Opening…' : 'Put a card on file'}
            </ButtonGhost>
            <p className="text-[13px] text-gray-600 text-center mt-1">Optional. Nothing is charged now — you type the card on Stripe's secure page.</p>
          </div>
        )
      )}

      {data?.portal_url && (
        <a href={data.portal_url} className="press block text-center text-[14px] font-bold text-brand-700 underline min-h-[48px] pt-3 no-print">
          {commercial ? 'Your account: sites, visits, invoices and papers' : 'See your account: schedule, invoices and rewards'}
        </a>
      )}

      {/* They asked for a week: the bid is still theirs to accept, and nothing is lost. */}
      {(snoozed || snoozeNote) && (
        <Panel tone="good">
          <p className="text-[15px] text-gray-900">{snoozed || snoozeNote}</p>
        </Panel>
      )}

      {/* A question, asked right here (owner, Oct 29 2026: "make it where the bids can be answered"). It lands on the
          office's Texts thread with this bid on it; the answer comes back by text. */}
      {!notReady && <AskOnBid token={token!} />}

      {/* The price and the one button, always on screen. */}
      {/* The free lawn walkthrough (owner, Oct 20 2026) — outlined, never above Accept. */}
      {!commercial && <LawnWalkthroughButton kind="bid" token={token!} />}

      {notReady ? (
        <Panel tone="warn">
          <p className="text-[15px] text-gray-900">This quote is still being put together. We will text you the moment it is ready.</p>
        </Panel>
      ) : (
        <StickyBar
          label={isContract ? 'Your price' : hasChoices ? 'Your total' : 'Total'}
          value={<>{headline.value}<span className="text-base font-bold text-gray-600"> {headline.unit}</span></>}
          sub={needsAnOption ? (bidOptions!.options.length > 1 ? 'Tap the one you want — you can take more than one.' : 'Tap the option above to take it.')
            : needsCard ? 'A card on file is required. Nothing is charged now.'
            : needsDeposit ? `${deposit.pct}% deposit, ${money(Number(deposit.amount))}, paid by card first. It comes off your final bill.`
            : (priceLines.length ? priceLines.join(' · ') : winterSplit ? `${money(winterSplit.now)} now · ${money(winterSplit.winter)} in winter — saves you ${money(winterOff)}` : winterOff > 0 ? `Winter work saves you ${money(winterOff)}.` : addedByThem > 0 ? 'Includes what you added above.' : deposit?.required ? 'Your deposit comes off the final bill.' : 'Nothing is charged by accepting.')}
        >
          <ButtonPrimary onClick={() => needsCard ? saveCard.mutate() : needsDeposit ? payDeposit.mutate() : setSheet(true)}
            disabled={saveCard.isLoading || payDeposit.isLoading || needsAnOption}>
            {needsAnOption ? (bidOptions!.options.length > 1 ? 'Pick an option above' : 'Tap the option above')
              : needsCard ? (saveCard.isLoading ? 'Opening…' : 'Put a card on file')
              : needsDeposit ? (payDeposit.isLoading ? 'Opening…' : `Pay ${money(Number(deposit.amount))} deposit`)
              : 'Sign and accept'}
          </ButtonPrimary>
        </StickyBar>
      )}

      {sheet && (
        <AcceptSheet
          quote={quote}
          headline={headline}
          extraLines={priceLines}
          signedName={signedName} setSignedName={setSignedName}
          referral={referral} setReferral={setReferral}
          showReferral={showReferral} setShowReferral={setShowReferral}
          verify={verify} code={code} setCode={setCode}
          verifyError={verifyError}
          busy={respond.isLoading || sendCode.isLoading || snooze.isLoading}
          canAccept={canAccept}
          onAccept={startAccept}
          onResend={(via) => sendCode.mutate(via)}
          onSnooze={() => snooze.mutate()}
          onDecline={() => respond.mutate('decline')}
          onClose={() => setSheet(false)}
        />
      )}
    </PublicPage>
  );
}

/* ────────────────────────────────────────────────────────────────────────
   Saying yes, one thing at a time.

   Sign, then the code we send. The referral box only appears if they ask for
   it, and it is gone once a 6-digit code is on screen — two codes at once is
   how the wrong one gets typed (owner, Sep 16 2026).
   ──────────────────────────────────────────────────────────────────────── */

function AcceptSheet({
  quote, headline, extraLines, signedName, setSignedName, referral, setReferral,
  showReferral, setShowReferral, verify, code, setCode, verifyError, busy, canAccept,
  onAccept, onResend, onSnooze, onDecline, onClose,
}: {
  quote: any; headline: { value: string; unit: string }; extraLines: string[];
  signedName: string; setSignedName: (v: string) => void;
  referral: string; setReferral: (v: string) => void;
  showReferral: boolean; setShowReferral: (v: boolean) => void;
  verify: { to: string; via: string; options?: { email?: string | null; sms?: string | null }; practice_code?: string | null } | null;
  code: string; setCode: (v: string) => void;
  verifyError: string | null; busy: boolean; canAccept: boolean;
  onAccept: () => void; onResend: (via?: 'sms' | 'email') => void; onSnooze: () => void; onDecline: () => void; onClose: () => void;
}) {
  const needsCode = !!verify && verify.via !== 'none';
  // "Not right now" opens a second step instead of declining outright (owner's rule: never lose a bid by accident).
  // One filled button at a time: while it is open, "Remind me in a week" is the filled one and Accept is off screen.
  const [notNow, setNotNow] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" aria-label="Accept this quote">
      <button className="absolute inset-0 bg-black/60" onClick={onClose} aria-label="Close" />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-[28px] sm:rounded-3xl max-h-[92vh] overflow-y-auto shadow-lift">
        <div className="sticky top-0 bg-white border-b-2 border-brand-100 px-5 py-4 flex items-start justify-between gap-3">
          <div>
            <H2>{notNow ? 'Not right now?' : needsCode ? 'One last step' : 'Sign and accept'}</H2>
            <p className="text-[14px] text-gray-700 mt-1 tabular-nums">
              {headline.value} {headline.unit}{extraLines.length ? ` · ${extraLines.join(' · ')}` : ''}
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="press flex-shrink-0 h-11 w-11 -mr-2 -mt-1 rounded-2xl flex items-center justify-center text-gray-600 hover:bg-brand-50">
            <X className="h-6 w-6" aria-hidden />
          </button>
        </div>

        {notNow ? (
          <div className="px-5 py-5 space-y-4">
            <div className="rounded-2xl border-2 border-gray-200 px-4 py-4 space-y-3">
              <p className="text-[15px] text-gray-900">No problem. We can check back with you in a week — your bid stays open until then, and nothing changes.</p>
              <ButtonPrimary onClick={onSnooze} disabled={busy}>{busy ? 'Saving' : 'Remind me in a week'}</ButtonPrimary>
              <ButtonGhost onClick={onDecline} disabled={busy}>I'm not interested</ButtonGhost>
            </div>
            {verifyError && <p className="text-[15px] font-semibold text-red-700">{verifyError}</p>}
            <button type="button" onClick={() => setNotNow(false)} disabled={busy}
              className="press w-full text-[14px] font-bold text-gray-600 underline min-h-[48px] disabled:opacity-50">Back</button>
          </div>
        ) : (
        <div className="px-5 py-5 space-y-4">
          {!needsCode && (
            <>
              {/* The typed name is the signature, on every bid (owner, Oct 20 2026: no bid is accepted unless signed). */}
              <label className="block">
                <span className="block text-[15px] font-bold text-gray-900 mb-1">Type your full name to sign</span>
                <input value={signedName} onChange={e => setSignedName(e.target.value)} placeholder="Your full name"
                  autoComplete="name" autoCapitalize="words"
                  className="w-full border-2 border-brand-200 rounded-2xl px-4 min-h-[56px] text-[17px] focus:outline-none focus:border-brand-600 focus:ring-4 focus:ring-brand-100" />
              </label>
              {showReferral ? (
                <label className="block">
                  <span className="block text-[15px] font-bold text-gray-900 mb-1">Their code</span>
                  <input value={referral} onChange={e => setReferral(e.target.value.toUpperCase())} placeholder="e.g. ELLIS19"
                    autoCapitalize="characters"
                    className="w-full border-2 border-brand-200 rounded-2xl px-4 min-h-[56px] text-[17px] tracking-wider focus:outline-none focus:border-brand-600 focus:ring-4 focus:ring-brand-100" />
                </label>
              ) : (
                <button type="button" onClick={() => setShowReferral(true)}
                  className="press text-[14px] font-bold text-brand-700 underline min-h-[48px]">
                  A neighbor sent me
                </button>
              )}
            </>
          )}

          {needsCode && (
            <div>
              <p className="text-[15px] text-gray-900">
                We {verify!.via === 'sms' ? 'texted' : 'emailed'} a 6-digit code to <b>{verify!.to}</b>. It confirms it is really you. Codes last 15 minutes.
              </p>
              {verify!.practice_code && <p className="mt-2 text-[14px] font-bold text-amber-700">Practice mode. The code is {verify!.practice_code}</p>}
              <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric" autoComplete="one-time-code" placeholder="000000" aria-label="6-digit code"
                className="mt-3 w-full border-2 border-brand-200 rounded-2xl px-4 min-h-[64px] text-3xl tracking-[0.35em] text-center font-black focus:outline-none focus:border-brand-600 focus:ring-4 focus:ring-brand-100" />
              {referral.trim() && <p className="mt-2 text-[14px] text-gray-700">Referral code {referral.trim()} is on your account.</p>}
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1">
                <button type="button" onClick={() => onResend(verify!.via as 'sms' | 'email')} disabled={busy}
                  className="press text-[14px] font-bold text-brand-700 underline min-h-[48px] disabled:opacity-50">Send a new code</button>
                {verify!.via === 'email' && verify!.options?.sms && (
                  <button type="button" onClick={() => onResend('sms')} disabled={busy}
                    className="press text-[14px] font-bold text-brand-700 underline min-h-[48px] disabled:opacity-50">Text it instead</button>
                )}
                {verify!.via === 'sms' && verify!.options?.email && (
                  <button type="button" onClick={() => onResend('email')} disabled={busy}
                    className="press text-[14px] font-bold text-brand-700 underline min-h-[48px] disabled:opacity-50">Email it instead</button>
                )}
              </div>
            </div>
          )}

          {verifyError && <p className="text-[15px] font-semibold text-red-700">{verifyError}</p>}

          <ButtonPrimary onClick={onAccept} disabled={!canAccept}>
            {busy ? 'Sending' : needsCode ? 'Confirm and accept' : 'Sign and accept'}
          </ButtonPrimary>
          {!verify && (
            <p className="text-[13px] text-gray-600 text-center">
              We text or email you a 6-digit code next.{quote?.require_deposit ? '' : ' Nothing is charged by accepting.'}
            </p>
          )}
          <button type="button" onClick={() => setNotNow(true)} disabled={busy}
            className="press w-full text-[14px] font-bold text-gray-600 underline min-h-[48px] disabled:opacity-50">Not right now</button>
        </div>
        )}
      </div>
    </div>
  );
}

/** "Have a question?" on the bid page: a box, one button, one line back. Nothing else on the page waits on it. */
function AskOnBid({ token }: { token: string }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [sent, setSent] = useState<string | null>(null);
  const ask = useMutation(async () => (await publicApi.post(`/quotes/public/${token}/ask`, { body: text.trim() })).data, {
    onSuccess: (d: any) => { setSent(d?.message || 'Sent — we will text you back.'); setText(''); },
  });
  const error = (ask.error as any)?.response?.data?.message || (ask.isError ? 'Could not send that — text us at the number at the top of the page.' : null);
  return (
    <Panel>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[15px] font-black uppercase tracking-tight text-gray-900">Have a question?</p>
          <p className="text-[14px] text-gray-700 mt-0.5">Ask it here and we text you back — no need to leave the page.</p>
        </div>
        {!open && !sent && <ButtonGhost block={false} onClick={() => setOpen(true)}>Ask</ButtonGhost>}
      </div>
      {sent && <p className="mt-3 rounded-2xl bg-brand-50 border border-brand-200 px-4 py-3 text-[15px] text-gray-900"><Check className="inline h-4 w-4 mr-1 -mt-0.5" aria-hidden />{sent} <button type="button" className="underline font-semibold ml-1" onClick={() => { setSent(null); setOpen(true); }}>Ask another</button></p>}
      {open && !sent && (
        <div className="mt-3 space-y-2">
          <textarea value={text} onChange={e => setText(e.target.value.slice(0, 600))} rows={3} maxLength={600}
            placeholder="For example: can the mulch wait until December? What does the spray cover?"
            className="w-full rounded-2xl border-2 border-gray-200 px-4 py-3 text-[16px] text-gray-900 focus:border-brand-600 focus:outline-none" />
          {error && <p className="text-[14px] text-red-700">{error}</p>}
          <div className="flex gap-2">
            <ButtonPrimary block={false} onClick={() => ask.mutate()} disabled={ask.isLoading || text.trim().length < 2}>{ask.isLoading ? 'Sending…' : 'Send'}</ButtonPrimary>
            <ButtonGhost block={false} onClick={() => { setOpen(false); setText(''); }}>Cancel</ButtonGhost>
          </div>
        </div>
      )}
    </Panel>
  );
}
